import { getCrimeDisplayName } from '../constants/crimes';

const extractNumber = (rank) => {
  if (typeof rank !== 'string') return rank;
  const match = rank.match(/\d+/);
  return match ? parseInt(match[0], 10) : rank;
};

export const sortData = (data, sortConfig) => {
  if (!sortConfig.key) return data;

  if (sortConfig.key === 'name' && data.length > 0 && data[0]?.county) {
    return [...data].sort((a, b) => {
      const aCounty = String(a.county || '').replace(/\s+County$/, '');
      const bCounty = String(b.county || '').replace(/\s+County$/, '');
      
      if (sortConfig.direction === 'asc') {
        return aCounty.localeCompare(bCounty); 
      } else {
        return bCounty.localeCompare(aCounty); 
      }
    });
  }

  if (sortConfig.key === 'name' && data.length > 0 && data[0]?.city) {
    return [...data].sort((a, b) => {
      const aCity = String(a.city || '');
      const bCity = String(b.city || '');
      
      if (sortConfig.direction === 'asc') {
        return aCity.localeCompare(bCity); 
      } else {
        return bCity.localeCompare(aCity); 
      }
    });
  }

  return [...data].sort((a, b) => {
    let comparison = 0;
    
    if (sortConfig.key.includes('_rate_diff')) {
      const valueA = a[sortConfig.key];
      const valueB = b[sortConfig.key];
      
      if (valueA === null && valueB === null) {
        const crimePrefix = sortConfig.key.replace(/_all_cities.*_rate_diff.*|_all_counties.*_rate_diff.*|_all_metros.*_rate_diff.*|_national_rate_diff.*|_state_rate_diff.*|_rate_diff.*/g, '');
        const rateKey = `${crimePrefix}_rate`;
        const rateA = parseFloat(a[rateKey]) || 0;
        const rateB = parseFloat(b[rateKey]) || 0;
        comparison = rateA - rateB;
      }
      else if (valueA === null) return 1;
      else if (valueB === null) return -1;
      else {
        comparison = parseFloat(valueA) - parseFloat(valueB);
        
        if (comparison === 0) {
          const crimePrefix = sortConfig.key.replace(/_all_cities.*_rate_diff.*|_all_counties.*_rate_diff.*|_all_metros.*_rate_diff.*|_national_rate_diff.*|_state_rate_diff.*|_rate_diff.*/g, '');
          const rateKey = `${crimePrefix}_rate`;
          const rateA = parseFloat(a[rateKey]) || 0;
          const rateB = parseFloat(b[rateKey]) || 0;
          comparison = rateA - rateB;
        }
      }
    } 
    else {
      if (a[sortConfig.key] === null) return 1;
      if (b[sortConfig.key] === null) return -1;

      if (sortConfig.key.endsWith('_count_prev_diff_pct')) {
        let pctA = a[sortConfig.key];
        let pctB = b[sortConfig.key];
        
        if (pctA === "Infinity" || pctA === "inf") pctA = Infinity;
        if (pctB === "Infinity" || pctB === "inf") pctB = Infinity;
        
        if (pctA === Infinity && pctB === Infinity) {
          const diffKey = sortConfig.key.replace('_pct', '');
          const diffA = parseFloat(a[diffKey]) || 0;
          const diffB = parseFloat(b[diffKey]) || 0;
          comparison = diffA - diffB;
        } else if (pctA === Infinity) {
          comparison = 1;
        } else if (pctB === Infinity) {
          comparison = -1; 
        } else {
          comparison = parseFloat(pctA) - parseFloat(pctB);
          
          if (comparison === 0) {
            const diffKey = sortConfig.key.replace('_pct', '');
            const diffA = parseFloat(a[diffKey]) || 0;
            const diffB = parseFloat(b[diffKey]) || 0;
            comparison = diffA - diffB;
          }
        }
      } 
      else if (sortConfig.key.includes('rank')) {
        const aValue = extractNumber(a[sortConfig.key]);
        const bValue = extractNumber(b[sortConfig.key]);
        comparison = aValue - bValue;
      } 
      else if (sortConfig.key === 'name') {
        comparison = a[sortConfig.key].localeCompare(b[sortConfig.key]);
      } 
      else {
        comparison = a[sortConfig.key] - b[sortConfig.key];
      }
    }
    
    return sortConfig.direction === 'asc' ? comparison : -comparison;
  });
}; 

export const requestSort = (key, sortConfig, onSortChange) => {
  let direction = 'desc';
  
  if (key === 'name' || key.includes('rank')) {
    direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
  } else {
    if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = 'asc';
    }
  }
  
  onSortChange({ key, direction });
  return { key, direction };
};

export const getSortIcon = (columnKey, sortConfig) => {
  const isActive = sortConfig.key === columnKey;
  return <SortIcon active={isActive} direction={isActive ? sortConfig.direction : null} />;
};

const SortIcon = ({ active, direction }) => (
  <div className={`sort-icon ${active ? 'active' : ''} ${direction || ''}`}>
    <div className="triangle up"></div>
    <div className="triangle down"></div>
  </div>
);

export const defaultSort = (selectedCrime, locationType = null, compareMode = null, rateViewMode = null) => {
  const crimePrefix = selectedCrime.toLowerCase();
  const isCountyOrCity = locationType === 'county' || locationType === 'city';
  const isMetro = locationType === 'metro';
  
  const base = {
    reports: { key: '', direction: 'desc' },
    totals: { key: `${crimePrefix}_count`, direction: 'desc' },
    trends: { key: `${crimePrefix}_count_prev_diff_pct`, direction: 'desc' },
    rates: { key: `${crimePrefix}_national_rate_diff`, direction: 'desc' },
    scores: { key: `composite_score`, direction: 'desc' }
  };
  
  if (isMetro) {
    let ratesKey = `${crimePrefix}_national_rate_diff`;
    if (rateViewMode?.rateType === 'metro') {
      ratesKey = `${crimePrefix}_all_metros_rate_diff`;
    }
    return {
      ...base,
      rates: { key: ratesKey, direction: 'desc' }
    };
  }
  
  if (!isCountyOrCity || !compareMode) return base;
  
  const locationTypePlural = locationType === 'county' ? 'counties' : 'cities';

  let ratesKey = `${crimePrefix}_national_rate_diff`;
  if (rateViewMode) {
    if (rateViewMode.rateType === locationType) {
      ratesKey = rateViewMode.rateScope === 'state' 
        ? `${crimePrefix}_all_${locationTypePlural}_state_rate_diff`
        : `${crimePrefix}_all_${locationTypePlural}_national_rate_diff`;
    } else {
      ratesKey = rateViewMode.rateScope === 'state'
        ? `${crimePrefix}_state_rate_diff`
        : `${crimePrefix}_national_rate_diff`;
    }
  } else {
    ratesKey = `${crimePrefix}_all_${locationTypePlural}_${compareMode}_rate_diff`;
  }
  
  return {
    ...base,
    rates: { 
      key: ratesKey, 
      direction: 'desc' 
    },
    scores: { 
      key: `${compareMode}_composite_score`, 
      direction: 'desc' 
    }
  };
};

/**
 * Canonical table_sort for aggregate "All …" URLs. Uses live sortConfig when its key
 * belongs to the current crime; otherwise defaultSort (e.g. React state can still hold
 * the previous crime's column key for one commit after crime_type changes).
 */
export function getAggregateTableSortForUrl({
  selectedCrime,
  activeTab,
  compareMode,
  rateViewMode,
  locationInfo,
  sortConfig,
}) {
  if (!locationInfo?.name || !locationInfo?.type) {
    return { tableSort: null, sortDir: null };
  }
  const locationType = locationInfo.type;
  const name = locationInfo.name;
  const isCountyOrCity = locationType === 'county' || locationType === 'city';
  const isAllLocationsNational =
    isCountyOrCity &&
    (name === 'All Counties' || name === 'All Cities');
  const effectiveCompareMode = isAllLocationsNational ? 'national' : compareMode;
  const canonical = defaultSort(
    selectedCrime,
    locationType,
    effectiveCompareMode,
    rateViewMode
  )[activeTab];

  if (!canonical?.key) {
    return { tableSort: null, sortDir: null };
  }

  const key = sortConfig?.key;
  if (!key) {
    return { tableSort: canonical.key, sortDir: canonical.direction };
  }

  if (key === canonical.key) {
    return { tableSort: key, sortDir: sortConfig.direction };
  }

  if (key === 'name') {
    return { tableSort: key, sortDir: sortConfig.direction };
  }

  if (activeTab === 'scores') {
    if (key.includes('composite_score')) {
      return { tableSort: key, sortDir: sortConfig.direction };
    }
    return { tableSort: canonical.key, sortDir: canonical.direction };
  }

  const crimePrefix = `${selectedCrime.toLowerCase()}_`;
  if (key.startsWith(crimePrefix)) {
    return { tableSort: key, sortDir: sortConfig.direction };
  }

  return { tableSort: canonical.key, sortDir: canonical.direction };
}

export const getSortOptions = (activeTab, locationPrefix, crimePrefix, selectedCrime, compareMode, isCountyOrCity, stateAbbr, rateViewMode, selectedSource) => {
  const getComparisonPrefix = () => {
    if (!isCountyOrCity) return '';
    
    const isAllNational = compareMode === 'national';
    
    if (activeTab === 'trends') {
      return isAllNational ? 'US ' : `${stateAbbr} `;
    }
    
    return compareMode === 'state' ? `${stateAbbr} ` : 'US ';
  };

  const comparisonPrefix = getComparisonPrefix();

  switch(activeTab) {
    case 'totals':
      return [
        { key: `${crimePrefix}_${isCountyOrCity && compareMode ? `${compareMode}_` : ''}count_rank`, label: `${comparisonPrefix}${locationPrefix} Rank` },
        { key: 'name', label: locationPrefix },
        { key: `${crimePrefix}_count`, label: `Total ${getCrimeDisplayName(selectedCrime, selectedSource)}` },
        { key: `${crimePrefix}_${isCountyOrCity && compareMode ? `${compareMode}_` : 'national_'}count_avg_diff`, label: `${comparisonPrefix}${locationPrefix} Avg Difference` },
        { key: `${crimePrefix}_${isCountyOrCity && compareMode ? `${compareMode}_` : 'national_'}count_avg_diff_pct`, label: `% ${comparisonPrefix}${locationPrefix} Avg Difference` }
      ];
    case 'trends':
      return [
        { key: `${crimePrefix}_${isCountyOrCity ? `${compareMode === 'national' ? 'national' : 'state'}_` : ''}trend_rank`, label: `${comparisonPrefix}${locationPrefix} Rank` },
        { key: 'name', label: locationPrefix },
        { key: `${crimePrefix}_count`, label: `Total ${getCrimeDisplayName(selectedCrime, selectedSource)}` },
        { key: `${crimePrefix}_count_prev`, label: 'Previous Total' },
        { key: `${crimePrefix}_count_prev_diff`, label: 'Change from Previous Total' },
        { key: `${crimePrefix}_count_prev_diff_pct`, label: '% Change from Previous Total' }
      ];
    case 'rates':
      if (!isCountyOrCity) {
        const isMetro = locationPrefix === 'Metro';
        if (isMetro) {
          if (rateViewMode?.rateType === 'metro') {
            return [
              { key: `${crimePrefix}_national_rate_rank`, label: `Metro Rank` },
              { key: 'name', label: locationPrefix },
              { key: 'population', label: `${locationPrefix} Population` },
              { key: `${crimePrefix}_rate`, label: `${getCrimeDisplayName(selectedCrime, selectedSource)} Rate` },
              { key: `${crimePrefix}_all_metros_rate_diff`, label: `Metro Rate Difference` },
              { key: `${crimePrefix}_all_metros_rate_diff_pct`, label: `% Metro Rate Difference` }
            ];
          } else {
            return [
              { key: `${crimePrefix}_national_rate_rank`, label: `Metro Rank` },
              { key: 'name', label: locationPrefix },
              { key: 'population', label: `${locationPrefix} Population` },
              { key: `${crimePrefix}_rate`, label: `${getCrimeDisplayName(selectedCrime, selectedSource)} Rate` },
              { key: `${crimePrefix}_national_rate_diff`, label: `National Rate Difference` },
              { key: `${crimePrefix}_national_rate_diff_pct`, label: `% National Rate Difference` }
            ];
          }
        }
        return [
          { key: `${crimePrefix}_rate_rank`, label: `${locationPrefix} Rank` },
          { key: 'name', label: locationPrefix },
          { key: 'population', label: `${locationPrefix} Population` },
          { key: `${crimePrefix}_rate`, label: `${getCrimeDisplayName(selectedCrime, selectedSource)} Rate` },
          { key: `${crimePrefix}_national_rate_diff`, label: `National Rate Difference` },
          { key: `${crimePrefix}_national_rate_diff_pct`, label: `% National Rate Difference` }
        ];
      } else {
        const peerFallback = locationPrefix === 'City' ? 'city' : 'county';
        const rateType = rateViewMode?.rateType ?? peerFallback;
        const rateScope = rateViewMode?.rateScope || 'state';
        
        let labelPrefix = '';
        let rankKeyPrefix = '';
        let diffKeyPrefix = '';
        
        if (rateType === 'county' || rateType === 'city') {
          const locationTypePlural = rateType === 'county' ? 'counties' : 'cities';
          const locationTypeLabel = rateType === 'county' ? 'County' : 'City';
          
          if (rateScope === 'state') {
            labelPrefix = `${stateAbbr} ${locationTypeLabel}`;
            rankKeyPrefix = `state_`;
            diffKeyPrefix = `all_${locationTypePlural}_state_`;
          } else {
            labelPrefix = `US ${locationTypeLabel}`;
            rankKeyPrefix = `national_`;
            diffKeyPrefix = `all_${locationTypePlural}_national_`;
          }
        } else { 
          if (rateScope === 'state') {
            labelPrefix = `${stateAbbr} State`;
            rankKeyPrefix = diffKeyPrefix = 'state_';
          } else {
            labelPrefix = 'US National';
            rankKeyPrefix = diffKeyPrefix = 'national_';
          }
        }
        
        return [
          { key: `${crimePrefix}_${rankKeyPrefix}rate_rank`, label: `${labelPrefix} Rank` }, 
          { key: 'name', label: locationPrefix },
          { key: 'population', label: `${locationPrefix} Population` },
          { key: `${crimePrefix}_rate`, label: `${getCrimeDisplayName(selectedCrime, selectedSource)} Rate` },
          { key: `${crimePrefix}_${diffKeyPrefix}rate_diff`, label: `${labelPrefix} Rate Difference` }, 
          { key: `${crimePrefix}_${diffKeyPrefix}rate_diff_pct`, label: `% ${labelPrefix} Rate Difference` }
        ];
      }
    case 'scores':
      return [
        { key: `${isCountyOrCity && compareMode ? `${compareMode}_` : ''}composite_score_rank`, label: `${comparisonPrefix}${locationPrefix} Rank (Composite)` },
        { key: 'name', label: locationPrefix },
        { key: `${isCountyOrCity && compareMode ? `${compareMode}_` : ''}composite_score`, label: `${comparisonPrefix}KRIME Score™ (Composite)` },
        { key: `${crimePrefix}_${isCountyOrCity && compareMode ? `${compareMode}_` : ''}score`, label: `${comparisonPrefix}${selectedCrime === 'all_crime' ? 'All ' : ''}${getCrimeDisplayName(selectedCrime, selectedSource)} Score (Non-Weighted)` },
        { key: `${crimePrefix}_${isCountyOrCity && compareMode ? `${compareMode}_` : ''}score_rank`, label: `${comparisonPrefix}${locationPrefix} Rank (Non-Weighted)` },
      ];
    default:
      return [];
  }
};

export const getMapRangeFromSortKey = (key) => {
  if (!key) return null;
  
  if (key.endsWith('composite_score')) return 'composite_score';
  if (key.endsWith('composite_score_rank')) return 'composite_score_rank';
  
  if (key.endsWith('_score') && !key.includes('composite')) return 'score';
  if (key.endsWith('_score_rank') && !key.includes('composite')) return 'rank';
  
  if (key.endsWith('_count_rank')) return 'rank';
  if (key.endsWith('_trend_rank')) return 'rank';
  if (key.endsWith('_rate_rank')) return 'rank';
  
  if (key.endsWith('_count_percentile')) return 'percentile';
  if (key.endsWith('_trend_percentile')) return 'percentile';
  if (key.endsWith('_rate_percentile')) return 'percentile';
  
  if (key.endsWith('_count') && !key.includes('_avg') && !key.includes('_prev')) return 'count';
  if (key.endsWith('_count_avg')) return 'count_avg';
  if (key.endsWith('_count_avg_diff')) return 'avg_diff';
  if (key.endsWith('_count_avg_diff_pct')) return 'avg_diff_pct';
  if (key.endsWith('_count_prev')) return 'prev_count';
  if (key.endsWith('_count_prev_diff')) return 'trend_diff';
  if (key.endsWith('_count_prev_diff_pct')) return 'trend_diff_pct';
  if (key.endsWith('_count_vs_avg')) return 'count_vs_avg';
  if (key.endsWith('_count_vs_prev')) return 'count_vs_prev';
  
  if (key.endsWith('_rate') && !key.endsWith('_rate_rank') && !key.includes('_national_') && !key.includes('_vs_')) return 'rate';
  if (key.includes('_national_rate') && !key.includes('_diff') && !key.includes('_vs_')) return 'national_rate';
  if (key.includes('_state_rate') && !key.includes('_diff') && !key.includes('_vs_')) return 'national_rate';
  if (key.includes('_all_counties_state_rate') && !key.includes('_diff') && !key.includes('_vs_')) return 'national_rate';
  if (key.includes('_all_counties_national_rate') && !key.includes('_diff') && !key.includes('_vs_')) return 'national_rate';
  if (key.includes('_all_cities_state_rate') && !key.includes('_diff') && !key.includes('_vs_')) return 'national_rate';
  if (key.includes('_all_cities_national_rate') && !key.includes('_diff') && !key.includes('_vs_')) return 'national_rate';
  if (key.includes('_all_metros_rate') && !key.includes('_diff') && !key.includes('_vs_')) return 'national_rate';
  
  if ((key.endsWith('_rate_diff') || key.includes('_rate_diff')) && !key.includes('_pct')) return 'rate_diff';
  if (key.endsWith('_rate_diff_pct') || key.includes('_rate_diff_pct')) return 'rate_diff_pct';
  if (key.includes('_rate_vs_')) return 'rate_vs_national_rate';
  
  return null;
};

export const getSortKeyFromMapRange = (range, selectedCrime, activeTab, compareMode, rateViewMode, isCountyOrCity, locationType) => {
  if (!range) return null;
  
  const crimePrefix = selectedCrime.toLowerCase();
  
  if (activeTab === 'rates') {
    if (locationType === 'metro') {
      if (rateViewMode?.rateType === 'metro') {
        const diffPrefix = `${crimePrefix}_all_metros_`;
        switch (range) {
          case 'rate': return `${crimePrefix}_rate`;
          case 'national_rate': return `${diffPrefix}rate_diff`; 
          case 'rate_diff': return `${diffPrefix}rate_diff`;
          case 'rate_diff_pct': return `${diffPrefix}rate_diff_pct`;
          case 'rate_vs_national_rate': return `${diffPrefix}rate_diff`;
          case 'rank': return `${crimePrefix}_national_rate_rank`; 
          case 'percentile': return `${crimePrefix}_national_rate_rank`;
          default: return '';
        }
      }
      const diffPrefix = `${crimePrefix}_national_`;
      switch (range) {
        case 'rate': return `${crimePrefix}_rate`;
        case 'national_rate': return `${diffPrefix}rate_diff`;
        case 'rate_diff': return `${diffPrefix}rate_diff`;
        case 'rate_diff_pct': return `${diffPrefix}rate_diff_pct`;
        case 'rate_vs_national_rate': return `${diffPrefix}rate_diff`;
        case 'rank': return `${crimePrefix}_national_rate_rank`; 
        case 'percentile': return `${crimePrefix}_national_rate_rank`;
        default: return '';
      }
    }
    
    if (isCountyOrCity) {
      const rateType = rateViewMode?.rateType || locationType;
      const rateScope = rateViewMode?.rateScope || 'state';
      if (rateType === 'county' || rateType === 'city') {
        const locationTypePlural = rateType === 'county' ? 'counties' : 'cities';
        const keyPrefix =
          rateScope === 'state'
            ? `${crimePrefix}_all_${locationTypePlural}_state_`
            : `${crimePrefix}_all_${locationTypePlural}_national_`;
        switch (range) {
          case 'rate': return `${crimePrefix}_rate`;
          case 'national_rate': return `${keyPrefix}rate_diff`;
          case 'rate_diff': return `${keyPrefix}rate_diff`;
          case 'rate_diff_pct': return `${keyPrefix}rate_diff_pct`;
          case 'rate_vs_national_rate': return `${keyPrefix}rate_diff`;
          case 'rank': return `${keyPrefix}rate_rank`;
          case 'percentile': return `${keyPrefix}rate_rank`;
          default: return '';
        }
      }
      const geoPrefix =
        rateScope === 'state' ? `${crimePrefix}_state_` : `${crimePrefix}_national_`;
      switch (range) {
        case 'rate': return `${crimePrefix}_rate`;
        case 'national_rate': return `${geoPrefix}rate_diff`;
        case 'rate_diff': return `${geoPrefix}rate_diff`;
        case 'rate_diff_pct': return `${geoPrefix}rate_diff_pct`;
        case 'rate_vs_national_rate': return `${geoPrefix}rate_diff`;
        case 'rank': return `${geoPrefix}rate_rank`;
        case 'percentile': return `${geoPrefix}rate_rank`;
        default: return '';
      }
    }

    const diffPrefix = `${crimePrefix}_national_`;
    switch (range) {
      case 'rate': return `${crimePrefix}_rate`;
      case 'national_rate': return `${diffPrefix}rate_diff`;
      case 'rate_diff': return `${diffPrefix}rate_diff`;
      case 'rate_diff_pct': return `${diffPrefix}rate_diff_pct`;
      case 'rate_vs_national_rate': return `${diffPrefix}rate_diff`;
      case 'rank': return `${crimePrefix}_rate_rank`; 
      case 'percentile': return `${crimePrefix}_rate_rank`;
      default: return '';
    }
  }

  if (activeTab === 'scores') {
    const cmodePrefix = isCountyOrCity && compareMode ? `${compareMode}_` : '';
    switch (range) {
      case 'score': return `${crimePrefix}_${cmodePrefix}score`;
      case 'composite_score': return `${cmodePrefix}composite_score`;
      case 'composite_score_rank': return `${cmodePrefix}composite_score_rank`;
      case 'rank': return `${crimePrefix}_${cmodePrefix}score_rank`;
      default: return '';
    }
  }
  
  const avgPrefix = isCountyOrCity && compareMode ? `${compareMode}_` : 'national_';
  const rankPrefix = isCountyOrCity && compareMode ? `${compareMode}_` : '';
  
  switch (range) {
    case 'count': return `${crimePrefix}_count`;
    case 'count_avg': return `${crimePrefix}_count`;
    case 'avg_diff': return `${crimePrefix}_${avgPrefix}count_avg_diff`;
    case 'avg_diff_pct': return `${crimePrefix}_${avgPrefix}count_avg_diff_pct`;
    case 'count_vs_avg': return `${crimePrefix}_${avgPrefix}count_avg_diff`;
    case 'prev_count': return `${crimePrefix}_count_prev`;
    case 'trend_diff': return `${crimePrefix}_count_prev_diff`;
    case 'trend_diff_pct': return `${crimePrefix}_count_prev_diff_pct`;
    case 'count_vs_prev': return `${crimePrefix}_count_prev_diff`;
    case 'rank':
      if (activeTab === 'trends') return `${crimePrefix}_${rankPrefix}trend_rank`;
      return `${crimePrefix}_${rankPrefix}count_rank`;
    case 'percentile':
      if (activeTab === 'trends') return `${crimePrefix}_${rankPrefix}trend_rank`;
      return `${crimePrefix}_${rankPrefix}count_rank`;
    default:
      return null;
  }
};